import React, { useState } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import PersonOffOutlined from '@mui/icons-material/PersonOffOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined';
import PauseCircleOutlined from '@mui/icons-material/PauseCircleOutlined';
import PlayCircleOutlined from '@mui/icons-material/PlayCircleOutlined';
import ReplayOutlined from '@mui/icons-material/ReplayOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { BsCalendar } from "react-icons/bs";
import { addAppointment, updateAppointment } from "../../../../../../services/AppointmentService";
import { withSnackbar } from "../../../../../../utils/SnackbarHOC";
import { removeLineBreak } from "../../../../../../utils/EditorUtil";
import Modal from "../../../../../../components/Modal/Modal";
import { AppButton } from "../../../../../../components/UI";
import { updateStatusMission } from "../../../../../../services/MissionService";
import { reactivateProcess } from "../../../../../../services/ProcessService";

const APPOINTMENT_STATUSES = {
  PROPALE: "Propale",
  EMBAUCHE: "Embauche/Integration",
  REFUSED_BY_CLIENT: "Refusé par client",
  REFUSED_BY_CANDIDATE: "Refusé par candidat",
  REFUSED_BY_GO_AND_DEV: "Refusé par Go&Dev",
  NO_SHOW: "No show",
  REACTIVATE: "Réactiver",
  DEACTIVATE: "Désactiver",
  REACTIVATE_PROCESS: "REACTIVATE_PROCESS",
};

const RED = { main: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' };
const SLATE = { main: '#475569', bg: '#f1f5f9', border: '#cbd5e1' };
const GREEN = { main: '#166534', bg: '#f0fdf4', border: '#86efac' };
const AMBER = { main: '#b45309', bg: '#fffbeb', border: '#fcd34d' };

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
    {children}
  </Typography>
);

const OptionCard = ({ option, selected, onSelect }) => {
  const { icon: Icon, label, description, color } = option;
  return (
    <Box
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      sx={{
        position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.5, borderRadius: '10px', cursor: 'pointer',
        transition: 'all 0.15s ease', userSelect: 'none',
        border: `1.5px solid ${selected ? color.border : '#e2e8f0'}`,
        background: selected ? color.bg : '#fff',
        boxShadow: selected ? `0 0 0 3px ${color.bg}` : 'none',
        '&:hover': { borderColor: color.border, background: selected ? color.bg : '#fafafa' },
        '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: 2 },
      }}
    >
      <Box sx={{ width: 34, height: 34, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: color.bg, color: color.main }}>
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0, pr: 2.5 }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: selected ? color.main : '#0f172a', lineHeight: 1.3 }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.4, mt: 0.25 }}>{description}</Typography>
      </Box>
      {selected && <CheckCircle sx={{ position: 'absolute', top: 8, right: 8, fontSize: 18, color: color.main }} />}
    </Box>
  );
};

const AppointmentModal = ({
  missionId,
  isPassive,
  appointment,
  processId,
  reloadData,
  processStep,
  setReloadData,
  lastAppointment,
  snackbarShowMessage,
  handleOpenModal,
  setMessageType,
  setCandidatInfo = () => {},
  candidatInfo = {}
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date());
  const [dateIntegration, setDateIntegration] = useState(null);
  const [status, setStatus] = useState("");

  const REFUSAL_STATUSES = [
    APPOINTMENT_STATUSES.REFUSED_BY_CLIENT,
    APPOINTMENT_STATUSES.REFUSED_BY_CANDIDATE,
    APPOINTMENT_STATUSES.REFUSED_BY_GO_AND_DEV,
    APPOINTMENT_STATUSES.NO_SHOW,
  ];
  const isProcessRefused = REFUSAL_STATUSES.includes(processStep);

  const refusalOptions = [
    { value: APPOINTMENT_STATUSES.REFUSED_BY_CLIENT, label: 'Refusé par client', icon: BusinessOutlined, color: RED,
      description: "Le client ne retient pas le candidat. Le message de réponse négative s'ouvrira ensuite." },
    { value: APPOINTMENT_STATUSES.REFUSED_BY_CANDIDATE, label: 'Refusé par candidat', icon: PersonOffOutlined, color: RED,
      description: 'Le candidat se retire du process.' },
    { value: APPOINTMENT_STATUSES.REFUSED_BY_GO_AND_DEV, label: 'Refusé par Go&Dev', icon: BlockOutlined, color: RED,
      description: 'Go&Dev arrête le process pour ce candidat.' },
    { value: APPOINTMENT_STATUSES.NO_SHOW, label: 'No show', icon: EventBusyOutlined, color: SLATE,
      description: "Le candidat ne s'est pas présenté au rendez-vous." },
  ];

  const otherOptions = [
    isPassive
      ? { value: APPOINTMENT_STATUSES.REACTIVATE, label: 'Réactiver la mission', icon: PlayCircleOutlined, color: GREEN,
          description: 'La mission repasse en active.' }
      : { value: APPOINTMENT_STATUSES.DEACTIVATE, label: 'Désactiver la mission', icon: PauseCircleOutlined, color: AMBER,
          description: 'La mission passe en passive pour tous les candidats.' },
    ...(isProcessRefused
      ? [{ value: APPOINTMENT_STATUSES.REACTIVATE_PROCESS, label: 'Réactiver le process', icon: ReplayOutlined, color: GREEN,
          description: `Le process est actuellement « ${processStep} ». Il redevient actif.` }]
      : []),
  ];

  const allOptions = [...refusalOptions, ...otherOptions];
  const selectedOption = allOptions.find((o) => o.value === status);

  const handleOpen = () => {
    setCandidatInfo();
    setStatus("");
    setOpen(true);
  };
  const handleClose = () => !loading && setOpen(false);

  const handleSelected = (newStatus) => {
    if (newStatus !== APPOINTMENT_STATUSES.EMBAUCHE) {
      setDateIntegration(null);
    }
    if ([APPOINTMENT_STATUSES.REFUSED_BY_CLIENT, APPOINTMENT_STATUSES.REFUSED_BY_CANDIDATE].includes(newStatus)) {
      setDate(new Date());
    }
    setStatus(newStatus);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (status === APPOINTMENT_STATUSES.REACTIVATE_PROCESS) {
        const response = await reactivateProcess(processId);
        snackbarShowMessage(response.data, "success", 4000);
        setReloadData(!reloadData);
        setOpen(false);
        return;
      }

      if (
          status === APPOINTMENT_STATUSES.REACTIVATE ||
          status === APPOINTMENT_STATUSES.DEACTIVATE
      ) {
        const activate = status === APPOINTMENT_STATUSES.REACTIVATE;
        const response = await updateStatusMission(missionId, activate);
        snackbarShowMessage(response.data, "success", 4000);
        setReloadData(!reloadData);
        setOpen(false);
        return;
      }

      const payload = {
        date,
        status,
        commentaire: removeLineBreak(""),
        dateIntegration,
        candidat: "",
        selected: []
      };

      const response = await addAppointment(processId, payload);
      if (status === APPOINTMENT_STATUSES.REFUSED_BY_CLIENT) {
        setMessageType("Réponse neg suite au process");
        handleOpenModal();
      }
      snackbarShowMessage(response.data);
      setReloadData(!reloadData);
      setOpen(false);
    } catch (error) {
      snackbarShowMessage(error.response?.data?.error?.detail || "Une erreur est survenue", "error", 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const response = await updateAppointment({ date, processId });
      snackbarShowMessage(response.data);
      setReloadData(!reloadData);
      setOpen(false);
    } catch (error) {
      snackbarShowMessage(error.response?.data?.error?.detail || "Une erreur est survenue", "error", 4000);
    } finally {
      setLoading(false);
    }
  };

  const contextItems = [
    candidatInfo?.client && ['Client', candidatInfo.client],
    candidatInfo?.mission && ['Mission', candidatInfo.mission],
    candidatInfo?.consultant && ['Talent manager', candidatInfo.consultant],
  ].filter(Boolean);

  const isRefusal = refusalOptions.some((o) => o.value === status);

  return (
    <>
      <Tooltip title="Issue du process">
        <IconButton size="small" onClick={handleOpen} sx={{ color: '#03407b' }}>
          <BsCalendar size={18} />
        </IconButton>
      </Tooltip>
      <Modal
        open={open}
        onClose={handleClose}
        title={appointment ? "Modifier le RDV" : "Issue du Process"}
        maxWidth="sm"
        actions={
          <>
            <AppButton variant="secondary" onClick={handleClose} disabled={loading}>
              Annuler
            </AppButton>
            <AppButton
              variant={isRefusal ? 'danger' : 'primary'}
              onClick={appointment ? handleUpdate : handleSubmit}
              disabled={!status}
              loading={loading}
            >
              {selectedOption ? `Confirmer : ${selectedOption.label}` : 'Choisissez une issue'}
            </AppButton>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {(contextItems.length > 0 || processStep) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {processStep && (
                <Chip size="small" label={`Étape actuelle : ${processStep}`}
                  sx={{ height: 24, fontWeight: 600, background: '#ebf4fb', color: '#03407b', border: '1px solid #aed6f1' }} />
              )}
              {contextItems.map(([label, value]) => (
                <Chip key={label} size="small" label={<><b>{label} :</b> {value}</>}
                  sx={{ height: 24, background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }} />
              ))}
            </Box>
          )}

          <Box role="radiogroup" aria-label="Refus">
            <SectionLabel>Arrêter le process</SectionLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              {refusalOptions.map((o) => (
                <OptionCard key={o.value} option={o} selected={status === o.value} onSelect={() => handleSelected(o.value)} />
              ))}
            </Box>
          </Box>

          <Box role="radiogroup" aria-label="Autres actions">
            <SectionLabel>Autres actions</SectionLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              {otherOptions.map((o) => (
                <OptionCard key={o.value} option={o} selected={status === o.value} onSelect={() => handleSelected(o.value)} />
              ))}
            </Box>
          </Box>

          {status === APPOINTMENT_STATUSES.DEACTIVATE && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', px: 1.5, py: 1.25, borderRadius: '10px', background: AMBER.bg, border: `1px solid ${AMBER.border}` }}>
              <InfoOutlined sx={{ fontSize: 18, color: AMBER.main, mt: 0.1 }} />
              <Typography sx={{ fontSize: '0.8rem', color: AMBER.main }}>
                Attention : cette action concerne <b>la mission entière</b>, pas seulement ce candidat.
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default withSnackbar(AppointmentModal);
